import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCredentialsContext } from "../context/CredentialsContext";
import { getExpirationStatus } from "../utils/expiration";
import usePendingDeletionRequests from "../hooks/usePendingDeletionRequests";
import "../styles/header.css";

export default function Header() {
    const navigate = useNavigate();
    const headerRef = useRef(null);
    const [signOutError, setSignOutError] = useState(null);
    const location = useLocation();
    const { authEnabled, user, signOut, role, roleLoading, session } = useAuth();
    const [signingOut, setSigningOut] = useState(false);
    const { credentials } = useCredentialsContext();
    const userInitial = (user?.email || "").charAt(0).toUpperCase();

    const onAuthPage = location.pathname.startsWith("/auth");
    const isInternal = role === "internal_staff";
    const { count: deletionCount, error: deletionError } = usePendingDeletionRequests(
        isInternal && !roleLoading && !onAuthPage ? session?.access_token : null,
        location.pathname
    );
    const expirationCount = useMemo(() => {
        return credentials.reduce((count, credential) => {
            const status = getExpirationStatus(credential.expires_at);
            if (status === "expired" || status === "expiring-soon") {
                return count + 1;
            }
            return count;
        }, 0);
    }, [credentials]);

    useEffect(() => {
        const closeMenus = (event) => {
            headerRef.current?.querySelectorAll("details[open]").forEach((menu) => {
                if (!event || event.key === "Escape" || !menu.contains(event.target)) {
                    menu.open = false;
                    if (event?.key === "Escape" && menu.contains(document.activeElement)) {
                        menu.querySelector("summary")?.focus();
                    }
                }
            });
        };
        const onKeyDown = (event) => { if (event.key === "Escape") closeMenus(event); };
        closeMenus();
        document.addEventListener("pointerdown", closeMenus);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", closeMenus);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [location.pathname]);

    if (onAuthPage) {
        return null;
    }

    async function handleSignOut() {
        if (!authEnabled || signingOut) return;
        setSigningOut(true);
        setSignOutError(null);
        try {
            await signOut();
            navigate("/auth", { replace: true });
        } catch (err) {
            setSignOutError("Unable to log out. Please try again.");
        } finally {
            setSigningOut(false);
        }
    }

    return (
        <header className="app-header-chrome" ref={headerRef}>
            <div className="header-inner">
            <div className="header-section header-left">
                <Link to="/" className="brand-logo-link" aria-label="Be Accredited Group — home">
                    <img className="brand-logo" src="/images/be+accredited+group.webp"
                        alt="Be Accredited Group" width="1473" height="304" />
                </Link>
                <div className="brand-text">
                    <span className="brand-name">Password Manager</span>
                    <span className="brand-sub">Customer accreditation logins</span>
                </div>
            </div>

            <nav className="header-section header-center" aria-label="Main navigation">
                <NavLink to="/" end className="header-nav-link">Credentials</NavLink>
                {isInternal && (
                    <details className="header-menu">
                        <summary className="pill-btn pill-secondary menu-trigger">
                            Admin <span className="menu-chevron" aria-hidden="true">⌄</span>
                            {expirationCount > 0 && (
                                <span className="pill-badge">{expirationCount}</span>
                            )}
                        </summary>
                        <div className="menu-panel">
                            <button
                                className="menu-item"
                                onClick={() => navigate("/invitations")}
                            >
                                Invitations
                            </button>
                            <button className="menu-item" onClick={() => navigate("/staff/invitations")}>Invite staff</button>
                            <button
                                className="menu-item"
                                onClick={() => navigate("/expiration-management")}
                            >
                                Expiry
                            </button>
                            <Link className="menu-item deletion-menu-link" to="/data-deletion-management">
                                Deletion requests
                                {deletionCount > 0 && <span className="pill-badge">{deletionCount}</span>}
                            </Link>
                        </div>
                    </details>
                )}
            </nav>

            <div className="header-section header-right">
                <button
                    className="pill-btn pill-primary"
                    onClick={() => navigate("/create")}
                >
                    <span className="pill-icon">＋</span>
                    <span>Add credential</span>
                </button>
                {authEnabled && user && (
                    <details className="header-menu user-menu">
                        <summary className="pill-btn pill-ghost menu-trigger">
                            <span className="user-avatar">{userInitial}</span>
                            <span className="menu-label">Account</span>
                            <span className="menu-chevron" aria-hidden="true">⌄</span>
                        </summary>
                        <div className="menu-panel">
                            <div className="menu-meta">
                                <span className="menu-email">{user.email}</span>
                                <span className="menu-role">
                                    {roleLoading
                                        ? "Role: loading"
                                        : role === "internal_staff" ? "Internal staff" : role === "client" ? "Client" : "Account member"}
                                </span>
                            </div>
                            <div className="menu-divider" />
                            <button
                                className="menu-item"
                                onClick={() => navigate("/profile")}
                            >
                                Profile
                            </button>

                        </div>
                    </details>
                )}
                {authEnabled && user && (
                    <button type="button" className="pill-btn logout-button"
                        onClick={handleSignOut} disabled={signingOut}>
                        {signingOut ? "Logging out…" : "Log out"}
                    </button>
                )}
                {authEnabled && !user && (
                    <button
                        className="pill-btn pill-ghost"
                        onClick={() => navigate("/auth")}
                    >
                        Sign in
                    </button>
                )}
            </div>
            {signOutError && <p className="header-error" role="alert">{signOutError}</p>}
            {isInternal && (deletionCount > 0 || deletionError) && (
                <div className="deletion-staff-alert" role="status">
                    <span>{deletionError
                        ? "Unable to check pending deletion requests."
                        : `${deletionCount} deletion ${deletionCount === 1 ? "request needs" : "requests need"} review.`}</span>
                    <Link to="/data-deletion-management">Review deletion requests</Link>
                </div>
            )}
            </div>
        </header>
    );
}
